<template>
  <panel-layout
    id="admin-user-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="adminUser" class="flex items-center space-x-2">
        <o-history-link :item="adminUser" model="Employee" icon="history" slim flat />
      </div>
    </template>

    <template #heading>
      <div v-if="adminUser" class="flex items-center space-x-2">
        <div class="w-18 h-18 relative">
          <o-avatar :items="[ adminUser ]" size="xl" />
          <div v-if="!adminUser.isNewRecord" class="inset-0 absolute rounded-full h-18 w-18 bg-black/15 backdrop-filter backdrop-blur-sm flex items-center justify-center opacity-0 hover:opacity-100 transition cursor-pointer" @click="handleUpdateImage">
            <o-icon icon="edit" class="text-white" />
          </div>
        </div>
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ adminUser.isNewRecord ? $t('pages.adminUser.edit.newUser') : adminUser.displayName }}
          </h2>
          <div v-if="!adminUser.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.adminUser.edit.userID', { id: adminUser.idEmployee })" />
          </div>
        </div>
      </div>
    </template>

    <div v-if="adminUser" class="h-full">
      <o-tabs
        ref="tabs"
        nav-class="bg-gray-50 h-11 border-b dark:bg-gray-900"
      >
        <o-tab :name="$t('general.details')" icon="customerEdit" scroll>
          <admin-user-details v-model:admin-user="adminUser" @update="handleUpdate" />
        </o-tab>
        <o-tab :name="$t('general.alerts')" icon="notification" scroll>
          <admin-user-notifications v-model:admin-user="adminUser" />
        </o-tab>
      </o-tabs>
    </div>

    <template #footer>
      <div v-if="adminUser">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              :disabled="!unsavedChanges"
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button :disabled="!unsavedChanges" variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="adminUser">
      <update-image-dialog
        ref="updateImageDialog"
        :admin-user="adminUser"
        @update:adminUser="handleUpdateImageSuccess"
      />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import AdminUserDetails from '@/components/adminUser/edit/Details.vue'
// @ts-ignore
import AdminUserNotifications from '@/components/adminUser/edit/Notifications.vue'

// @ts-ignore
import UpdateImageDialog from '@/components/adminUser/actions/Image.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  OTabs,
  OTab,
  OAlert,
  OAvatar,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{
  id?: string
  tab?: string
}>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const adminUser = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const tabs = ref<any>()
const confirmDialog = ref<any>()
const updateImageDialog = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(adminUser)

const fetchAdminUser = async (id?: string) => {
  loading.value = true

  try {
    if (!id) {
      adminUser.value = (await $api.adminUser.defaults({} as any)).data
    } else {
      adminUser.value = (await $api.adminUser.get(id)).data
    }
    snapshot()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleUpdateImageSuccess = (updatedAdminUser: any) => {
  adminUser.value = updatedAdminUser
  snapshot()
  $bus.emit.updated('AdminUser', updatedAdminUser)
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    if (!adminUser.value.isNewRecord) {
      delete adminUser.value.email
    }
    const result = (await $api.adminUser.save(adminUser.value)).data

    if (result) {
      adminUser.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.adminUser.edit.success'),
        text: $i18n.t('pages.adminUser.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('AdminUser', result)

      if (close) {
        panel.close(true)
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleUpdateImage = () => {
  updateImageDialog.value.open()
}

const handleUpdate = () => {
  fetchAdminUser(props.id)
  $bus.emit.updated('AdminUser', adminUser.value)
}

watch(() => props.id, async (id) => {
  await fetchAdminUser(id)

  if (props.tab) {
    tabs.value?.selectTab('#' + props.tab)
  }

  loading.value = false
}, { immediate: true })
</script>
